
import { useState, createContext, useContext, ReactNode, useEffect } from 'react';

interface PriceAlert {
  id: number;
  productId: number;
  productName: string;
  productImage: string;
  currentPrice: number;
  targetPrice: number;
  retailer: string;
  createdAt: Date;
  isActive: boolean;
}

interface PriceHistory {
  date: string;
  price: number;
}

interface TrackedProduct {
  id: number;
  name: string;
  image: string;
  currentPrice: number;
  lowestPrice: number;
  highestPrice: number;
  retailer: string;
  priceHistory: PriceHistory[];
  alerts: PriceAlert[];
  addedAt: Date;
}

interface PriceMonitorContextType {
  trackedProducts: TrackedProduct[];
  addToTracking: (product: Omit<TrackedProduct, 'priceHistory' | 'alerts' | 'addedAt' | 'lowestPrice' | 'highestPrice'>) => void;
  removeFromTracking: (id: number) => void;
  setTargetPrice: (productId: number, targetPrice: number) => void;
  removeAlert: (productId: number, alertId: number) => void;
  getTotalTracked: () => number;
  getActiveAlerts: () => PriceAlert[];
  isTracked: (productId: number) => boolean;
}

const PriceMonitorContext = createContext<PriceMonitorContextType | undefined>(undefined);

export const usePriceMonitor = () => {
  const context = useContext(PriceMonitorContext);
  if (!context) {
    throw new Error('usePriceMonitor must be used within a PriceMonitorProvider');
  }
  return context;
};

export const PriceMonitorProvider = ({ children }: { children: ReactNode }) => {
  const [trackedProducts, setTrackedProducts] = useState<TrackedProduct[]>([
    {
      id: 1,
      name: 'iPad Pro M2',
      image: 'https://readdy.ai/api/search-image?query=Modern%20sleek%20iPad%20Pro%20M2%20tablet%20computer%20with%20advanced%20display%20technology%2C%20minimalist%20design%2C%20pure%20white%20background%2C%20centered%20composition%2C%20professional%20product%20photography%20style%2C%20no%20shadows%2C%20high-tech%20aesthetic&width=200&height=200&seq=track1&orientation=squarish',
      currentPrice: 1099,
      lowestPrice: 999,
      highestPrice: 1299,
      retailer: 'Amazon',
      priceHistory: [
        { date: '2024-01-01', price: 1199 },
        { date: '2024-01-15', price: 1149 },
        { date: '2024-02-01', price: 1099 },
        { date: '2024-02-15', price: 1049 },
        { date: '2024-03-01', price: 1099 },
      ],
      alerts: [
        {
          id: 1,
          productId: 1,
          productName: 'iPad Pro M2',
          productImage: 'https://readdy.ai/api/search-image?query=Modern%20sleek%20iPad%20Pro%20M2%20tablet%20computer%20with%20advanced%20display%20technology%2C%20minimalist%20design%2C%20pure%20white%20background%2C%20centered%20composition%2C%20professional%20product%20photography%20style%2C%20no%20shadows%2C%20high-tech%20aesthetic&width=200&height=200&seq=track1&orientation=squarish',
          currentPrice: 1099,
          targetPrice: 999,
          retailer: 'Amazon',
          createdAt: new Date('2024-02-01'),
          isActive: true,
        }
      ],
      addedAt: new Date('2024-01-01'),
    },
    {
      id: 5,
      name: 'iPhone 15 Pro',
      image: 'https://readdy.ai/api/search-image?query=Latest%20iPhone%2015%20Pro%20smartphone%20with%20titanium%20finish%2C%20advanced%20camera%20system%2C%20premium%20materials%2C%20sleek%20design%2C%20pure%20white%20background%2C%20centered%20composition%2C%20professional%20product%20photography%20style%2C%20no%20shadows%2C%20cutting-edge%20mobile%20technology&width=200&height=200&seq=track2&orientation=squarish',
      currentPrice: 999,
      lowestPrice: 949,
      highestPrice: 1199,
      retailer: 'Best Buy',
      priceHistory: [
        { date: '2024-01-01', price: 1199 },
        { date: '2024-01-15', price: 1099 },
        { date: '2024-02-01', price: 1049 },
        { date: '2024-02-15', price: 999 },
        { date: '2024-03-01', price: 999 },
      ],
      alerts: [],
      addedAt: new Date('2024-01-15'),
    }
  ]);

  const addToTracking = (product: Omit<TrackedProduct, 'priceHistory' | 'alerts' | 'addedAt' | 'lowestPrice' | 'highestPrice'>) => {
    setTrackedProducts(prev => {
      const exists = prev.find(p => p.id === product.id);
      if (exists) return prev;
      
      return [...prev, {
        ...product,
        lowestPrice: product.currentPrice,
        highestPrice: product.currentPrice,
        priceHistory: [{ date: new Date().toISOString().split('T')[0], price: product.currentPrice }],
        alerts: [],
        addedAt: new Date(),
      }];
    });
  };

  const removeFromTracking = (id: number) => {
    setTrackedProducts(prev => prev.filter(p => p.id !== id));
  };

  const setTargetPrice = (productId: number, targetPrice: number) => {
    setTrackedProducts(prev => prev.map(product => {
      if (product.id === productId) {
        const newAlert: PriceAlert = {
          id: Date.now(),
          productId,
          productName: product.name,
          productImage: product.image,
          currentPrice: product.currentPrice,
          targetPrice,
          retailer: product.retailer,
          createdAt: new Date(),
          isActive: true,
        };
        return {
          ...product,
          alerts: [...product.alerts, newAlert],
        };
      }
      return product;
    }));
  };

  const removeAlert = (productId: number, alertId: number) => {
    setTrackedProducts(prev => prev.map(product => {
      if (product.id === productId) {
        return {
          ...product,
          alerts: product.alerts.filter(a => a.id !== alertId),
        };
      }
      return product;
    }));
  };

  const getTotalTracked = () => trackedProducts.length;

  const getActiveAlerts = () => {
    return trackedProducts.flatMap(p => p.alerts.filter(a => a.isActive));
  };

  const isTracked = (productId: number) => {
    return trackedProducts.some(p => p.id === productId);
  };

  return (
    <PriceMonitorContext.Provider value={{
      trackedProducts,
      addToTracking,
      removeFromTracking,
      setTargetPrice,
      removeAlert,
      getTotalTracked,
      getActiveAlerts,
      isTracked,
    }}>
      {children}
    </PriceMonitorContext.Provider>
  );
};

export { PriceMonitorContext };
